import React, { useEffect, useRef, useState } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AlertTriangle, ChevronDown, ChevronUp, Eye, Files, GripVertical, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import useSetlistOrder from './useSetlistOrder';

// Alleen verticaal slepen: de lijst is een kolom.
const verticalOnly = (transform) => (transform ? { ...transform, x: 0 } : transform);

const SCREEN_READER_INSTRUCTIONS = {
  draggable: 'Druk op spatie om dit onderdeel op te pakken. Gebruik de pijltoetsen omhoog en omlaag om te verplaatsen, spatie om neer te zetten, Escape om te annuleren.',
};

const SetlistRow = ({ item, index, count, meta, artist, canManage, disabled, onMove, onRemove, onOpen, registerButton }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: item.id, disabled: !canManage || disabled });
  const style = { transform: CSS.Transform.toString(verticalOnly(transform)), transition };
  const label = item.title || 'onderdeel';
  return <li ref={setNodeRef} style={style} data-item-id={item.id}
    className={`flex items-center gap-2 rounded-md border bg-white p-2 sm:gap-3 sm:p-3 ${isDragging ? 'relative z-10 border-teal-400 shadow-lg ring-2 ring-teal-200' : 'border-slate-200'}`}>
    {canManage && <button type="button" ref={setActivatorNodeRef} {...attributes} {...listeners} disabled={disabled}
      aria-label={`Sleep ${label}, positie ${index + 1} van ${count}`}
      className="flex h-11 w-9 shrink-0 cursor-grab touch-none select-none items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-600 active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-40">
      <GripVertical className="h-5 w-5" aria-hidden="true" />
    </button>}
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold" aria-hidden="true">{index + 1}</span>
    <div className="min-w-0 flex-1">
      {item.song_id
        ? <button type="button" className="block max-w-full truncate text-left font-medium text-teal-700 hover:underline" onClick={() => onOpen(item)}>{item.title}</button>
        : <p className="truncate font-medium text-slate-900">{item.title}</p>}
      <p className="truncate text-xs text-slate-500">{[artist, meta].filter(Boolean).join(' · ')}</p>
    </div>
    {item.pdf_link_url && <a href={item.pdf_link_url} target="_blank" rel="noreferrer" aria-label={item.pdf_link_label || `Bekijk PDF van ${label}`}><Button variant="ghost" size="icon" tabIndex={-1} className="h-10 w-10"><Files className="h-4 w-4 text-teal-700" /></Button></a>}
    {item.song_id && <Button variant="ghost" size="icon" className="h-10 w-10" aria-label={`Open ChordPro van ${label}`} onClick={() => onOpen(item)}><Eye className="h-4 w-4" /></Button>}
    {canManage && <div className="flex shrink-0 items-center">
      <Button ref={(node) => registerButton(item.id, 'up', node)} type="button" variant="ghost" size="icon" className="h-11 w-11 sm:h-10 sm:w-10" disabled={disabled || index === 0}
        aria-label={`Verplaats ${label} omhoog`} onClick={() => onMove(index, index - 1, item.id, 'up')}><ChevronUp className="h-4 w-4" /></Button>
      <Button ref={(node) => registerButton(item.id, 'down', node)} type="button" variant="ghost" size="icon" className="h-11 w-11 sm:h-10 sm:w-10" disabled={disabled || index === count - 1}
        aria-label={`Verplaats ${label} omlaag`} onClick={() => onMove(index, index + 1, item.id, 'down')}><ChevronDown className="h-4 w-4" /></Button>
      <Button type="button" variant="ghost" size="icon" className="h-11 w-11 sm:h-10 sm:w-10" disabled={disabled} aria-label={`Verwijder ${label}`} onClick={() => onRemove(item)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
    </div>}
  </li>;
};

/**
 * Sleepbare setlistlijst. Eén sleepbare plek per rij (het handvat), plus omhoog/omlaag-knoppen en toetsenbordbediening
 * als gelijkwaardig alternatief. Alle volgordelogica zit in useSetlistOrder; dit component rendert en kondigt aan.
 */
const SetlistEditor = ({ service, canManage, busy = false, getItemMeta, artistBySongId = {}, onServiceChange, onOpenItem, onRemoveItem, emptyText }) => {
  const { items, saving, error, move, retry, dismissError } = useSetlistOrder({
    serviceId: service.id, serverItems: service.setlist || [], onServerService: onServiceChange,
  });
  const [announcement, setAnnouncement] = useState('');
  const buttons = useRef(new Map());
  const pendingFocus = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const announce = (item, to, count) => setAnnouncement(`${item.title || 'Onderdeel'} verplaatst naar positie ${to + 1} van ${count}`);

  const handleButtonMove = (from, to, id, direction) => {
    const item = items[from];
    if (!move(from, to)) return;
    announce(item, to, items.length);
    pendingFocus.current = { id, direction, atEdge: direction === 'up' ? to === 0 : to === items.length - 1 };
  };

  // Na een verplaatsing via knoppen blijft de focus bij het verplaatste onderdeel; staat het aan de rand, dan op de andere knop.
  useEffect(() => {
    const target = pendingFocus.current;
    if (!target) return;
    pendingFocus.current = null;
    const wanted = target.atEdge ? (target.direction === 'up' ? 'down' : 'up') : target.direction;
    buttons.current.get(`${target.id}:${wanted}`)?.focus();
  });

  const registerButton = (id, direction, node) => {
    if (node) buttons.current.set(`${id}:${direction}`, node); else buttons.current.delete(`${id}:${direction}`);
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = items.findIndex((item) => item.id === active.id);
    const to = items.findIndex((item) => item.id === over.id);
    if (from < 0 || to < 0) return;
    const item = items[from];
    if (move(from, to)) announce(item, to, items.length);
  };

  const position = (id) => items.findIndex((item) => item.id === id) + 1;
  const announcements = {
    onDragStart: ({ active }) => `Opgepakt: onderdeel ${position(active.id)} van ${items.length}.`,
    onDragOver: ({ over }) => (over ? `Nu boven positie ${position(over.id)} van ${items.length}.` : 'Niet boven een geldige positie.'),
    onDragEnd: ({ over }) => (over ? `Neergezet op positie ${position(over.id)} van ${items.length}.` : 'Neergezet; volgorde ongewijzigd.'),
    onDragCancel: () => 'Verplaatsen geannuleerd; volgorde ongewijzigd.',
  };

  return <div>
    <div role="status" aria-live="polite" className="sr-only">{announcement}</div>
    {error && <div role="alert" className="mb-3 flex flex-wrap items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1">{error.message}</span>
      {error.kind === 'failed' && <Button type="button" size="sm" variant="outline" onClick={retry}>Opnieuw proberen</Button>}
      <Button type="button" size="sm" variant="ghost" onClick={dismissError}>Sluiten</Button>
    </div>}
    {items.length === 0
      ? <p className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500">{emptyText}</p>
      : <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} accessibility={{ announcements, screenReaderInstructions: SCREEN_READER_INSTRUCTIONS }}>
        <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
          <ol className="space-y-2" aria-label="Dienstvolgorde">
            {items.map((item, index) => <SetlistRow key={item.id} item={item} index={index} count={items.length} meta={getItemMeta?.(item)} artist={artistBySongId[item.song_id]}
              canManage={canManage} disabled={busy} onMove={handleButtonMove} onRemove={onRemoveItem} onOpen={onOpenItem} registerButton={registerButton} />)}
          </ol>
        </SortableContext>
      </DndContext>}
    {saving && <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500" role="status"><Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />Volgorde opslaan…</p>}
  </div>;
};

export default SetlistEditor;
